import { defineStore } from 'pinia'
import type { Prices } from '@shc/nuxt-shc-ui-layer/components/Cart/Mini/cartMini'
import type {
    CartError,
    CartItem,
    CartSalesCoupon,
    CartSeparateDiscountRule,
    CartTotals,
    FreeDelivery,
    MarketplaceCart,
    Maybe,
    ProductImage,
    CartShippingAddress as SourceCartShippingAddress,
    Payment as SourcePayment,
} from '../graphql/types/graphql'

type CartShippingAddress = PartialDeep<SourceCartShippingAddress | null>
type Payment = PartialDeep<SourcePayment | null>

export const useCartStore = defineStore('cart', {
    state: (): State => ({
        products: [],
        totals: {
            currency: {
                name: '',
                shortName: '',
                symbol: '',
            },
            discounts: [],
            shipping: 0,
            subtotal: 0,
            tax: 0,
            total: 0,
        },
        availableShippingMethods: [],
        availablePaymentMethods: [],
        separateDiscountsRule: [],
        actionLoader: false,
        freeDelivery: null,
        marketplace: [],
        salesCoupon: null,
        errors: [],
        cartReady: false,
    }),

    actions: {
        setProducts(newProducts: Maybe<CartItem>[]) {
            this.products = newProducts
        },
        setTotals(newTotals: CartTotals) {
            this.totals = newTotals
        },
        setCoupons(newCoupons: Maybe<CartSeparateDiscountRule>[]) {
            this.separateDiscountsRule = newCoupons
        },
        setSalesCoupon(newCoupons: Maybe<CartSalesCoupon>) {
            this.salesCoupon = newCoupons
        },
        setAvailableShippingMethods(newAvailableShippingMethods: CartShippingAddress[]) {
            this.availableShippingMethods = newAvailableShippingMethods
        },
        setAvailablePaymentMethods(newAvailablePaymentMethods: Payment[]) {
            this.availablePaymentMethods = newAvailablePaymentMethods
        },
        setFreeDelivery(newFreeDelivery: Maybe<FreeDelivery>) {
            this.freeDelivery = newFreeDelivery
        },
        setMarketplace(newMarketplace: Maybe<MarketplaceCart>[]) {
            this.marketplace = newMarketplace
        },
        setErrors(errors: Maybe<CartError>[]) {
            this.errors = errors
        },
        setCartReady(state: boolean) {
            this.cartReady = state
        },
    },
})

interface State {
    products: Maybe<CartItem>[]
    totals: CartTotals
    availableShippingMethods: CartShippingAddress[]
    availablePaymentMethods: Payment[]
    separateDiscountsRule: Maybe<CartSeparateDiscountRule>[]
    actionLoader: boolean
    freeDelivery: Maybe<FreeDelivery>
    marketplace: Maybe<MarketplaceCart>[]
    salesCoupon?: Maybe<CartSalesCoupon>
    errors?: Maybe<Maybe<CartError>[]>
    cartReady: boolean
}

interface PriceCurrency {
    name: string
    shortName: string
    symbol: string
}

export interface Totals {
    total?: number
    value?: number
    currency: PriceCurrency
    subtotal: number
    shipping: number
    tax: number
}

export interface Products {
    id: string
    quantity: number
    prices: Prices
    product: Product
}

export interface Product {
    id: string
    sku: string
    name: string
    url: string
    inStock?: boolean | null
    images: ProductImage[]
}
